import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { FONT_B, FONT_H, GRADIENT_BG, GRADIENT_HIGHLIGHT, PILL_SHADOW, CARD_BORDER, LILAC, INK, SIGNUP_PATH, fadeUp, stagger, VIEWPORT_ONCE } from './theme';

/* ── Buttons ── */
export function GradientButton({ to, children, block = false, testId, onClick }) {
  const cls = `rounded-full font-extrabold text-white text-[15px] py-[15px] text-center hover:-translate-y-0.5 transition-transform ${block ? 'block w-full sm:w-auto sm:inline-block sm:px-11' : 'inline-block px-11'}`;
  const style = { ...FONT_B, background: GRADIENT_BG, ...PILL_SHADOW };
  if (onClick) {
    return <button onClick={onClick} className={cls} style={style} data-testid={testId}>{children}</button>;
  }
  return <Link to={to || SIGNUP_PATH} className={cls} style={style} data-testid={testId}>{children}</Link>;
}

export function TextLink({ to, children, testId }) {
  return (
    <Link to={to} className="text-sm font-bold hover:opacity-70 transition-opacity" style={{ ...FONT_B, color: '#6C3CE0' }} data-testid={testId}>
      {children}
    </Link>
  );
}

/* ── Shells ── */
export function Section({ tone = 'white', children, className = '', id, testId, narrow = false }) {
  const bg = tone === 'lilac' ? LILAC : tone === 'ink' ? INK : '#fff';
  return (
    <section id={id} className={`px-5 sm:px-8 py-10 md:py-16 ${className}`} style={{ background: bg }} data-testid={testId}>
      <div className={`${narrow ? 'max-w-[760px]' : 'max-w-[1100px]'} mx-auto`}>{children}</div>
    </section>
  );
}

export function Eyebrow({ children, className = '' }) {
  return (
    <p className={`text-[11px] font-extrabold uppercase tracking-[0.18em] m-0 mb-2 ${className}`} style={{ ...FONT_B, color: '#E91E8C' }}>
      {children}
    </p>
  );
}

export function SectionTitle({ children, eyebrow, center = true, lead, className = '' }) {
  return (
    <div className={`${center ? 'text-center max-w-[760px] mx-auto' : ''} ${className}`}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className="font-extrabold tracking-tight text-[23px] md:text-[32px] leading-tight m-0" style={FONT_H}>{children}</h2>
      {lead && <p className="text-sm md:text-base text-gray-600 leading-relaxed mt-3" style={FONT_B}>{lead}</p>}
    </div>
  );
}

/* ── Photo (webp with a jpeg fallback, sized to avoid layout shift) ── */
const PHOTO_SIZES = {
  '/marketing/bar.jpg': [868, 1300],
  '/marketing/golf-society.jpg': [1040, 1300],
  '/marketing/golf-course.jpg': [926, 1300],
  '/marketing/workplace.jpg': [1200, 675],
  '/marketing/friends.jpg': [1200, 675],
  '/marketing/football.jpg': [1200, 900],
  '/marketing/racing.jpg': [1200, 806],
  '/marketing/spreadsheet.jpg': [1200, 800],
  '/marketing/notebook.jpg': [1200, 800],
  '/marketing/sports-club.jpg': [1200, 800],
};

export function Photo({ src, alt, className = '', eager = false, hidden = false }) {
  const [w, h] = PHOTO_SIZES[src] || [1200, 800];
  return (
    <picture className="contents">
      <source type="image/webp" srcSet={src.replace(/\.jpg$/, '.webp')} />
      <img
        src={src}
        alt={hidden ? '' : alt}
        aria-hidden={hidden || undefined}
        width={w}
        height={h}
        className={className}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
      />
    </picture>
  );
}

/* ── Page hero ── */
export function PageHero({ crumb, eyebrow, title, highlight, lead, primary, secondary, image, imageAlt, align = 'left', variant = 'light' }) {
  const dark = variant === 'dark';
  const centered = align === 'center';

  const inner = (
    <>
      {crumb && (
        <p className={`text-[11px] font-bold mb-2.5 ${dark ? 'text-white/60' : 'text-gray-500'}`} style={FONT_B}>
          <Link to="/" className={dark ? 'text-white/60 hover:text-white' : 'text-gray-500 hover:text-gray-800'}>Home</Link> / {crumb}
        </p>
      )}
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h1
        className="font-black tracking-tight text-[28px] sm:text-4xl lg:text-[44px] leading-[1.14] m-0 mb-2.5"
        style={{ ...FONT_H, color: dark ? '#fff' : undefined }}
      >
        {title}{highlight ? ' ' : ''}{highlight && <span style={GRADIENT_HIGHLIGHT}>{highlight}</span>}
      </h1>
      {lead && (
        <p className={`text-sm sm:text-base md:text-[17px] leading-relaxed mb-5 md:mb-7 ${dark ? 'text-white/80' : 'text-gray-600'}`} style={FONT_B}>
          {lead}
        </p>
      )}
      {(primary || secondary) && (
        <div className={`flex flex-col sm:flex-row gap-4 items-stretch sm:items-center ${centered ? 'sm:justify-center' : ''}`}>
          {primary && <GradientButton to={primary.to} block testId="hero-primary-cta">{primary.label}</GradientButton>}
          {secondary && (
            <span className={`${centered ? 'text-center' : ''} py-1`}>
              <TextLink to={secondary.to} testId="hero-secondary-cta">{secondary.label} &rarr;</TextLink>
            </span>
          )}
        </div>
      )}
    </>
  );

  if (dark) {
    return (
      <section className="relative overflow-hidden px-5 sm:px-8 pt-24 pb-12 md:pt-32 md:pb-20 text-center" style={{ background: INK }} data-testid="marketing-page-hero">
        {image && <Photo src={image} alt="" hidden className="absolute inset-0 w-full h-full object-cover opacity-[0.28]" eager />}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(12,10,20,0.5), rgba(12,10,20,0.92))' }} aria-hidden />
        <motion.div initial="hidden" animate="visible" variants={stagger} className="relative max-w-[760px] mx-auto">
          <motion.div variants={fadeUp}>{inner}</motion.div>
        </motion.div>
      </section>
    );
  }

  return (
    <section
      className="px-5 sm:px-8 pt-24 pb-10 md:pt-28 md:pb-14"
      style={{ background: 'linear-gradient(180deg, #F5F0FF 0%, #ffffff 100%)' }}
      data-testid="marketing-page-hero"
    >
      <div className={`max-w-[1100px] mx-auto grid gap-7 md:gap-14 items-center ${image ? 'md:grid-cols-[1.1fr_1fr]' : ''}`}>
        <motion.div initial="hidden" animate="visible" variants={stagger} className={centered ? 'text-center max-w-[760px] mx-auto' : ''}>
          <motion.div variants={fadeUp}>{inner}</motion.div>
        </motion.div>
        {image && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="rounded-[16px] md:rounded-[20px] overflow-hidden h-[180px] sm:h-[280px] md:h-[340px]"
            style={{ boxShadow: '0 24px 60px rgba(108,60,224,0.18)' }}
          >
            <Photo src={image} alt={imageAlt} eager className="w-full h-full object-cover block" />
          </motion.div>
        )}
      </div>
    </section>
  );
}

/* ── Lilac feature cards ── */
export function CardGrid({ items, cols = 3, testId }) {
  const grid = cols === 1 ? '' : cols === 2 ? 'sm:grid-cols-2' : cols === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-2 lg:grid-cols-3';
  return (
    <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger} className={`grid grid-cols-1 ${grid} gap-3 md:gap-5`} data-testid={testId}>
      {items.map((it) => (
        <motion.div
          key={it.title}
          variants={fadeUp}
          className="rounded-[16px] md:rounded-[20px] border p-[18px] md:p-7 hover:-translate-y-1 transition-transform duration-300"
          style={{ background: LILAC, ...CARD_BORDER }}
          data-testid={`card-${it.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
        >
          {it.eyebrow && <Eyebrow className="!text-[10px] !tracking-[0.14em] !mb-1.5">{it.eyebrow}</Eyebrow>}
          <h3 className="font-bold text-[15px] md:text-[17px] mb-1.5" style={FONT_H}>{it.title}</h3>
          <p className="text-[13px] md:text-sm text-gray-600 leading-relaxed m-0" style={FONT_B}>{it.desc}</p>
          {it.meta && <p className="text-[11px] text-gray-500 mt-2.5 m-0" style={FONT_B}>{it.meta}</p>}
        </motion.div>
      ))}
    </motion.div>
  );
}

/* ── Alternating format cards (Pool Types) ── */
export function FormatCards({ items, testId }) {
  return (
    <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger} className="grid grid-cols-1 md:grid-cols-2 gap-3.5 md:gap-5" data-testid={testId}>
      {items.map((it, i) => {
        const lilac = i % 2 === 0;
        return (
          <motion.div
            key={it.title}
            variants={fadeUp}
            className="rounded-[18px] md:rounded-[20px] border p-5 md:p-7"
            style={lilac ? { background: LILAC, ...CARD_BORDER } : { background: '#fff', borderColor: '#f3f4f6', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
            data-testid={`format-${it.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          >
            <Eyebrow className="!text-[10px] !tracking-[0.14em] !mb-1.5">{it.sport}</Eyebrow>
            <h2 className="font-extrabold text-[18px] md:text-xl mb-2" style={FONT_H}>{it.title}</h2>
            <p className="text-[13px] md:text-sm text-gray-700 leading-relaxed m-0 mb-2.5" style={FONT_B}>{it.desc}</p>
            <p className="text-[11px] md:text-xs text-gray-500 m-0" style={FONT_B}>
              <strong className="text-gray-900">Best for:</strong> {it.bestFor} · <strong className="text-gray-900">Runs:</strong> {it.runs}
            </p>
          </motion.div>
        );
      })}
    </motion.div>
  );
}

/* ── Photo cards with gradient caption (home sports) ── */
export function MediaCards({ items, testId }) {
  return (
    <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-5" data-testid={testId}>
      {items.map((it) => (
        <motion.div key={it.title} variants={fadeUp} className="relative rounded-[16px] overflow-hidden" data-testid={`media-${it.title.toLowerCase()}`}>
          <Photo src={it.image} alt={it.alt} className="w-full h-[120px] md:h-[220px] object-cover block" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(11,10,20,0) 20%, rgba(11,10,20,0.82))' }} />
          <div className="absolute left-4 bottom-3 text-white">
            <div className="font-extrabold text-base md:text-lg" style={FONT_H}>{it.title}</div>
            <div className="text-xs md:text-[13px] opacity-85" style={FONT_B}>{it.desc}</div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}

/* ── Emoji cards (home "Who it's for") ── */
export function EmojiCards({ items, testId }) {
  return (
    <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger} className={`grid grid-cols-2 gap-3 md:gap-5 ${items.length === 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`} data-testid={testId}>
      {items.map((it) => (
        <motion.div key={it.title} variants={fadeUp}>
          <Link
            to={it.to}
            className="block h-full bg-white border rounded-[16px] p-4 md:p-6 hover:-translate-y-1 hover:shadow-md transition-all"
            style={{ borderColor: '#f3f4f6' }}
            data-testid={`audience-${it.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
          >
            <div className="text-xl md:text-2xl mb-2">{it.emoji}</div>
            <h3 className="font-bold text-sm md:text-base mb-1.5" style={FONT_H}>{it.title}</h3>
            <p className="text-xs md:text-sm text-gray-600 leading-snug m-0" style={FONT_B}>{it.desc}</p>
          </Link>
        </motion.div>
      ))}
    </motion.div>
  );
}

/* ── Pill links (stack on mobile, per the mobile design) ── */
export function ChipLinks({ items, testId }) {
  return (
    <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-4 sm:justify-center" data-testid={testId}>
      {items.map((it) => (
        <Link
          key={it.label}
          to={it.to}
          className="bg-white border border-gray-100 rounded-full py-3 sm:px-6 text-center text-[13px] sm:text-sm font-bold text-gray-900 hover:-translate-y-0.5 hover:shadow-md transition-all"
          style={FONT_B}
          data-testid={`chip-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
        >
          {it.emoji ? `${it.emoji} ` : ''}{it.label}
        </Link>
      ))}
    </div>
  );
}

/* ── Static pill row (Masters tournaments) ── */
export function PillRow({ items, testId }) {
  return (
    <div className="flex flex-wrap gap-2 justify-center" data-testid={testId}>
      {items.map((it) => (
        <span
          key={it.label}
          className="rounded-full px-4 py-2 text-xs font-bold"
          style={it.active
            ? { ...FONT_B, background: GRADIENT_BG, color: '#fff' }
            : { ...FONT_B, background: '#fff', border: '1px solid #e5e7eb', color: '#374151' }}
          data-testid={`pill-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
        >
          {it.label}
        </span>
      ))}
    </div>
  );
}

/* ── Numbered rows (society steps) ── */
export function NumberedRows({ items, testId }) {
  return (
    <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger} className="grid grid-cols-1 md:grid-cols-2 gap-2.5 md:gap-4" data-testid={testId}>
      {items.map((it, i) => (
        <motion.div key={it.title} variants={fadeUp} className="flex gap-3.5 items-start bg-white rounded-[14px] p-3.5 md:p-5 border" style={{ borderColor: '#f3f4f6' }}>
          <span className="w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-white text-xs font-extrabold" style={{ background: GRADIENT_BG, ...FONT_H }}>{i + 1}</span>
          <div>
            <h3 className="text-sm md:text-base font-bold m-0 mb-0.5" style={FONT_H}>{it.title}</h3>
            <p className="text-xs md:text-sm text-gray-600 m-0" style={FONT_B}>{it.desc}</p>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}

/* ── Before/after comparison cards (vs spreadsheets) ── */
export function VsCards({ items, testId }) {
  return (
    <motion.div initial="hidden" whileInView="visible" viewport={VIEWPORT_ONCE} variants={stagger} className="grid grid-cols-1 md:grid-cols-2 gap-2.5 md:gap-4" data-testid={testId}>
      {items.map((it) => (
        <motion.div key={it.label} variants={fadeUp} className="border rounded-[14px] p-4 md:p-5" style={{ borderColor: '#f3f4f6' }} data-testid={`vs-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
          <p className="font-extrabold text-[13px] md:text-sm m-0 mb-2" style={FONT_H}>{it.label}</p>
          <p className="text-xs md:text-[13px] text-gray-500 m-0 mb-1.5" style={FONT_B}>&#10007; {it.before}</p>
          <p className="text-xs md:text-[13px] text-gray-900 rounded-lg px-2.5 py-2 m-0" style={{ ...FONT_B, background: LILAC }}>&#10003; {it.after}</p>
        </motion.div>
      ))}
    </motion.div>
  );
}

/* ── FAQ accordion ── */
export function FaqList({ items, testId }) {
  const [open, setOpen] = useState(null);
  return (
    <div className="max-w-[760px] mx-auto" data-testid={testId}>
      {items.map((f) => (
        <div key={f.q} className="border-b border-gray-200 last:border-0">
          <button
            onClick={() => setOpen(open === f.q ? null : f.q)}
            className="w-full flex items-center justify-between gap-4 py-3.5 md:py-4 text-left"
            data-testid={`faq-item-${f.q.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 40)}`}
          >
            <span className="text-sm md:text-base font-semibold" style={FONT_H}>{f.q}</span>
            <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open === f.q ? 'rotate-180' : ''}`} />
          </button>
          {open === f.q && (
            <motion.p initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="pb-4 text-[13px] md:text-sm text-gray-600 leading-relaxed m-0" style={FONT_B}>{f.a}</motion.p>
          )}
        </div>
      ))}
    </div>
  );
}

export function FaqGroupHeading({ children, first = false }) {
  return <h2 className={`font-extrabold text-base md:text-lg mb-1 ${first ? 'mt-0' : 'mt-6'}`} style={{ ...FONT_H, color: '#6C3CE0' }}>{children}</h2>;
}

/* ── Framed screenshot ── */
export function ScreenShot({ src, alt, testId }) {
  const webp = src.replace(/\.png$/, '.webp');
  return (
    <div className="rounded-[14px] overflow-hidden border border-gray-200 max-w-[860px] mx-auto" style={{ boxShadow: '0 16px 40px rgba(108,60,224,0.16)' }} data-testid={testId}>
      <picture>
        <source type="image/webp" srcSet={webp} />
        <img src={src} alt={alt} className="w-full block" loading="lazy" decoding="async" />
      </picture>
    </div>
  );
}

/* ── Three gradient metrics ── */
export function MetricRow({ items, testId }) {
  return (
    <div className="flex justify-between text-center gap-2" data-testid={testId}>
      {items.map((m) => (
        <div key={m.label} className="flex-1">
          <div className="font-black text-lg md:text-2xl" style={{ ...FONT_H, ...GRADIENT_HIGHLIGHT }}>{m.metric}</div>
          <div className="text-[10px] md:text-xs text-gray-500" style={FONT_B}>{m.label}</div>
        </div>
      ))}
    </div>
  );
}

/* ── Closing CTA band ── */
export function CtaBand({ title, lead, ctaLabel = 'Start a Pool', ctaTo, tone = 'lilac', links = false, testId = 'marketing-cta-band' }) {
  return (
    <section className="px-5 sm:px-8 py-9 md:py-16 text-center" style={{ background: tone === 'lilac' ? LILAC : '#fff' }} data-testid={testId}>
      <h2 className="font-black tracking-tight text-[22px] md:text-4xl m-0 mb-2 max-w-[720px] mx-auto" style={FONT_H}>{title}</h2>
      {lead && <p className="text-[13px] md:text-base text-gray-600 leading-relaxed max-w-[480px] mx-auto mb-4 md:mb-7" style={FONT_B}>{lead}</p>}
      <GradientButton to={ctaTo} block testId="cta-band-primary">{ctaLabel}</GradientButton>
      {links && (
        <p className="text-[13px] mt-6 flex flex-wrap gap-x-2 gap-y-1 justify-center" style={FONT_B}>
          <TextLink to="/masters-pools" testId="cta-band-masters">Run a Masters pool</TextLink>
          <span className="text-gray-400">·</span>
          <TextLink to="/vs-spreadsheets" testId="cta-band-vs">Why not a spreadsheet?</TextLink>
          <span className="text-gray-400">·</span>
          <TextLink to="/faq" testId="cta-band-faq">FAQ</TextLink>
        </p>
      )}
    </section>
  );
}

/* ── Gradient CTA card (guide) ── */
export function GradientCtaCard({ title, ctaLabel = 'Start a Pool', ctaTo, testId = 'gradient-cta-card' }) {
  return (
    <div className="rounded-[18px] p-6 md:p-8 text-center text-white" style={{ background: GRADIENT_BG }} data-testid={testId}>
      <h2 className="font-black text-[19px] md:text-2xl m-0 mb-2" style={FONT_H}>{title}</h2>
      <Link
        to={ctaTo || SIGNUP_PATH}
        className="inline-block mt-1.5 font-extrabold text-sm bg-white rounded-full px-7 py-3 hover:-translate-y-0.5 transition-transform"
        style={{ ...FONT_B, color: '#6C3CE0' }}
        data-testid="gradient-cta-button"
      >
        {ctaLabel}
      </Link>
    </div>
  );
}

/* ── Testimonial ── */
export function Quote({ quote, name, org, testId }) {
  const ini = name.split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  return (
    <blockquote className="m-0 mx-auto max-w-[760px] rounded-[20px] p-6 md:p-8 border" style={{ background: LILAC, ...CARD_BORDER }} data-testid={testId}>
      <p className="text-gray-700 text-[15px] md:text-[17px] leading-[1.7] m-0 mb-5" style={FONT_B}>&ldquo;{quote}&rdquo;</p>
      <footer className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-full flex items-center justify-center text-white text-[13px] font-extrabold flex-shrink-0" style={{ background: '#6C3CE0', ...FONT_H }}>{ini}</span>
        <span>
          <span className="block font-extrabold text-sm" style={FONT_H}>{name}</span>
          <span className="block text-xs text-gray-500" style={FONT_B}>{org}</span>
        </span>
      </footer>
    </blockquote>
  );
}
